
import React, { useState, useEffect } from 'react';
import { useCart } from '../context/CartContext';
import { Page } from '../types';
import { ShieldCheckIcon } from '../assets/icons';

interface CheckoutPageProps {
    onNavigate: (page: Page) => void;
}

const FIREARM_CATS = ['Pistola', 'Revólver', 'Espingarda', 'Fuzil', 'Carabina', 'Rifle'];

const CheckoutPage: React.FC<CheckoutPageProps> = ({ onNavigate }) => {
    const { cartItems, totalAmount, clearCart } = useCart();
    const [step, setStep] = useState<'form' | 'redirecting' | 'approved' | 'pending' | 'failure'>('form');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [formData, setFormData] = useState({ name: '', email: '', phone: '', cpf: '', cr: '', address: '' });

    const hasFirearm = cartItems.some(i => FIREARM_CATS.includes(i.category));

    // Retorno do MercadoPago: /checkout?status=approved&ref=...
    useEffect(() => {
        const p = new URLSearchParams(window.location.search);
        const status = p.get('status');
        if (status === 'approved') { clearCart(); setStep('approved'); }
        else if (status === 'pending') { clearCart(); setStep('pending'); }
        else if (status === 'failure') { setStep('failure'); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handlePay = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true); setError('');
        try {
            const res = await fetch('/api/checkout/create-preference', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    items: cartItems.map(i => ({ name: i.name, quantity: i.quantity, price: i.price })),
                    payer: { name: formData.name, email: formData.email, phone: formData.phone, cpf: formData.cpf },
                    hasFirearm,
                }),
            });
            const data = await res.json();
            if (!res.ok || !data.init_point) {
                setError(data.error || 'Não foi possível iniciar o pagamento. Tente novamente.');
                setLoading(false);
                return;
            }
            setStep('redirecting');
            window.location.href = data.init_point; // Checkout Pro (Pix + cartão)
        } catch (err) {
            setError('Erro de conexão. Tente novamente.');
            setLoading(false);
        }
    };

    const inputClass = "w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-arsenal-red focus:ring-1 focus:ring-arsenal-red focus:outline-none transition-colors";
    const labelClass = "block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2";

    if (cartItems.length === 0 && step === 'form') {
        return (
            <div className="bg-gray-900 min-h-[calc(100vh-200px)] flex flex-col items-center justify-center p-6 text-center">
                <h2 className="text-2xl font-bold text-white mb-4">Seu carrinho está vazio.</h2>
                <button onClick={() => onNavigate('marketplace')} className="text-arsenal-red hover:underline font-bold">
                    Voltar ao Marketplace
                </button>
            </div>
        );
    }

    return (
        <div className="bg-gray-900 py-12 min-h-screen">
            <div className="container mx-auto px-6 max-w-4xl">
                <button onClick={() => onNavigate('marketplace')} className="mb-8 text-gray-400 hover:text-white flex items-center gap-2 transition-colors">
                    &larr; Voltar
                </button>

                <h1 className="text-3xl md:text-4xl font-black text-white mb-8">Finalizar Compra</h1>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Resumo */}
                    <div className="lg:col-span-1 order-2 lg:order-1">
                        <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700 top-24 sticky">
                            <h3 className="text-lg font-bold text-white mb-4">Resumo do Pedido</h3>
                            <ul className="space-y-4 mb-6">
                                {cartItems.map(item => (
                                    <li key={item.id} className="flex gap-3">
                                        <div className="w-12 h-12 bg-gray-900 rounded-lg overflow-hidden flex-shrink-0">
                                            <img src={item.imageUrls[0]} alt={item.name} className="w-full h-full object-cover" />
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-xs text-white font-bold line-clamp-1">{item.name}</p>
                                            <p className="text-xs text-gray-400">{item.quantity}x {item.price}</p>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                            <div className="border-t border-gray-700 pt-4">
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-400 text-sm">Total</span>
                                    <span className="text-xl font-black text-white">
                                        R$ {totalAmount.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </span>
                                </div>
                                <p className="text-[11px] text-gray-500 mt-2">Pix ou cartão parcelado. Juros do parcelamento por conta do comprador.</p>
                            </div>
                        </div>
                    </div>

                    {/* Form / Estados */}
                    <div className="lg:col-span-2 order-1 lg:order-2">
                        {step === 'form' && (
                            <div className="bg-gray-800/50 rounded-2xl p-6 md:p-8 border border-gray-700">
                                {hasFirearm && (
                                    <div className="mb-6 rounded-xl border border-amber-600/40 bg-amber-500/10 p-4 text-sm text-amber-200">
                                        <strong className="font-bold">Compra de arma — entrega futura.</strong> Você paga agora e garante o produto, mas a <strong>retirada só ocorre após a autorização do Exército/PF</strong>. Nossa equipe cuida da documentação (CR/CRAF) e do despachante.
                                    </div>
                                )}
                                <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                                    <UserIcon className="h-5 w-5 text-arsenal-red" /> Seus Dados
                                </h3>
                                <form onSubmit={handlePay} className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className={labelClass}>Nome Completo</label>
                                            <input required type="text" name="name" value={formData.name} onChange={handleInputChange} className={inputClass} />
                                        </div>
                                        <div>
                                            <label className={labelClass}>Email</label>
                                            <input required type="email" name="email" value={formData.email} onChange={handleInputChange} className={inputClass} />
                                        </div>
                                        <div>
                                            <label className={labelClass}>Telefone / WhatsApp</label>
                                            <input required type="tel" name="phone" value={formData.phone} onChange={handleInputChange} className={inputClass} />
                                        </div>
                                        <div>
                                            <label className={labelClass}>CPF</label>
                                            <input required type="text" name="cpf" value={formData.cpf} onChange={handleInputChange} className={inputClass} placeholder="000.000.000-00" />
                                        </div>
                                        <div>
                                            <label className={labelClass}>CR {hasFirearm ? '' : '(Opcional)'}</label>
                                            <input type="text" name="cr" value={formData.cr} onChange={handleInputChange} className={inputClass} placeholder="Para agilizar a documentação" />
                                        </div>
                                        <div>
                                            <label className={labelClass}>Cidade / UF</label>
                                            <input required type="text" name="address" value={formData.address} onChange={handleInputChange} className={inputClass} />
                                        </div>
                                    </div>

                                    {error && <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3">{error}</p>}

                                    <div className="pt-4">
                                        <button type="submit" disabled={loading}
                                            className="w-full bg-arsenal-red hover:bg-red-700 disabled:opacity-60 text-white font-bold py-4 rounded-xl shadow-lg shadow-red-900/30 transition-all hover:-translate-y-1 flex items-center justify-center gap-2">
                                            {loading ? 'Abrindo pagamento…' : <><ShieldCheckIcon className="h-5 w-5" /> Pagar com Pix ou Cartão</>}
                                        </button>
                                        <p className="text-center text-xs text-gray-500 mt-3">
                                            Você será levado ao ambiente seguro do MercadoPago para concluir o pagamento.
                                        </p>
                                    </div>
                                </form>
                            </div>
                        )}

                        {step === 'redirecting' && (
                            <div className="bg-gray-800/50 rounded-2xl p-12 border border-gray-700 flex flex-col items-center justify-center text-center h-full min-h-[400px]">
                                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-arsenal-red mb-6"></div>
                                <h3 className="text-xl font-bold text-white">Abrindo o pagamento seguro…</h3>
                                <p className="text-gray-400 mt-2">Você está sendo levado ao MercadoPago.</p>
                            </div>
                        )}

                        {step === 'approved' && (
                            <div className="bg-gray-800/50 rounded-2xl p-12 border border-gray-700 flex flex-col items-center justify-center text-center h-full min-h-[400px]">
                                <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mb-6">
                                    <ShieldCheckIcon className="h-10 w-10 text-green-500" />
                                </div>
                                <h3 className="text-2xl font-black text-white mb-2">Compra confirmada! 🦁</h3>
                                <p className="text-gray-400 mb-6 max-w-md">
                                    Recebemos seu pagamento. Nossa equipe já foi avisada e vai entrar em contato pelo WhatsApp/email.
                                    Para <strong className="text-amber-300">armas</strong>, a entrega ocorre após a autorização do Exército/PF — cuidamos da documentação (CR/CRAF) e do despachante.
                                </p>
                                <button onClick={() => onNavigate('marketplace')} className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-3 px-8 rounded-xl transition-colors">
                                    Voltar ao Marketplace
                                </button>
                            </div>
                        )}

                        {step === 'pending' && (
                            <div className="bg-gray-800/50 rounded-2xl p-12 border border-gray-700 flex flex-col items-center justify-center text-center h-full min-h-[400px]">
                                <div className="w-20 h-20 bg-amber-500/10 rounded-full flex items-center justify-center mb-6">
                                    <ShieldCheckIcon className="h-10 w-10 text-amber-500" />
                                </div>
                                <h3 className="text-2xl font-black text-white mb-2">Pagamento em processamento</h3>
                                <p className="text-gray-400 mb-6 max-w-md">
                                    Assim que o pagamento for confirmado (Pix costuma ser na hora), seu pedido é liberado e nossa equipe entra em contato.
                                </p>
                                <button onClick={() => onNavigate('marketplace')} className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-3 px-8 rounded-xl transition-colors">
                                    Voltar ao Marketplace
                                </button>
                            </div>
                        )}

                        {step === 'failure' && (
                            <div className="bg-gray-800/50 rounded-2xl p-12 border border-gray-700 flex flex-col items-center justify-center text-center h-full min-h-[400px]">
                                <h3 className="text-2xl font-black text-white mb-2">Pagamento não concluído</h3>
                                <p className="text-gray-400 mb-6 max-w-md">O pagamento não foi finalizado. Você pode tentar de novo — seu carrinho continua salvo.</p>
                                <button onClick={() => setStep('form')} className="bg-arsenal-red hover:bg-red-700 text-white font-bold py-3 px-8 rounded-xl transition-colors">
                                    Tentar novamente
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const UserIcon = ({ className }: { className?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
    </svg>
);

export default CheckoutPage;
